import { useEffect, useState } from "react";
import { FileText, Download, Share2, Image as ImageIcon, Video, Package, Calendar, Phone, Mail, User } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { PageHero } from "@/components/public/Shared";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { formatDate } from "@/lib/helpers";

export default function MediaCenter({ defaultTab = "press" }) {
  const [press, setPress] = useState([]);
  const [media, setMedia] = useState([]);

  useEffect(() => {
    api.get("/press-releases").then((r) => setPress(r.data));
    api.get("/media-assets").then((r) => setMedia(r.data));
  }, []);

  const photos = media.filter((m) => m.type === "photo");
  const videos = media.filter((m) => m.type === "video");
  const materials = media.filter((m) => m.type === "material");
  const notify = () => toast.info("Demo: berkas akan diunduh pada sistem produksi.");

  return (
    <div>
      <PageHero eyebrow="Untuk Wartawan & Media" title="Media Center Bawaslu"
        description="Informasi dan materi resmi untuk kebutuhan publikasi media." />
      <div className="container-page py-10">
        <Tabs defaultValue={defaultTab} className="w-full">
          <TabsList className="flex flex-wrap h-auto bg-surface p-1.5 gap-1" data-testid="media-tabs">
            <TabsTrigger value="press" data-testid="tab-press">Press Release</TabsTrigger>
            <TabsTrigger value="foto" data-testid="tab-foto">Dokumentasi Foto</TabsTrigger>
            <TabsTrigger value="video" data-testid="tab-video">Dokumentasi Video</TabsTrigger>
            <TabsTrigger value="materi" data-testid="tab-materi">Materi Publikasi</TabsTrigger>
            <TabsTrigger value="kontak" data-testid="tab-kontak">Kontak Humas</TabsTrigger>
          </TabsList>

          <TabsContent value="press" className="mt-6">
            <div className="space-y-3">
              {press.map((p) => (
                <div key={p.id} className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`press-${p.id}`}>
                  <div className="h-11 w-11 rounded-lg bg-brandred/10 text-brandred flex items-center justify-center shrink-0"><FileText className="h-5 w-5" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-xs text-slate-400 mb-1">
                      <span className="bg-navy/5 text-navy px-2 py-0.5 rounded font-semibold">{p.category}</span>
                      <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {formatDate(p.date)}</span>
                    </div>
                    <h3 className="font-bold text-navy">{p.title}</h3>
                    <p className="text-sm text-slate-500 line-clamp-1">{p.summary}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button size="sm" variant="outline" onClick={notify} data-testid="media-center-download"><Download className="h-4 w-4 mr-1" /> Unduh</Button>
                    <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard?.writeText(window.location.href); toast.success("Tautan disalin"); }}><Share2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="foto" className="mt-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {photos.map((p) => (
                <div key={p.id} className="group relative rounded-xl overflow-hidden border border-slate-200">
                  <img src={p.url} alt={p.title} className="h-40 w-full object-cover group-hover:scale-105 transition-transform" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-3">
                    <span className="text-white text-xs font-semibold">{p.title}</span>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="video" className="mt-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {videos.map((v) => (
                <div key={v.id} className="rounded-xl overflow-hidden border border-slate-200 bg-white">
                  <div className="relative h-44 bg-slate-900">
                    <img src={v.url} alt={v.title} className="h-full w-full object-cover opacity-70" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="h-14 w-14 rounded-full bg-brandred/90 flex items-center justify-center"><Video className="h-6 w-6 text-white" /></div>
                    </div>
                  </div>
                  <div className="p-4"><h4 className="font-bold text-navy text-sm">{v.title}</h4><p className="text-xs text-slate-400 mt-1">{formatDate(v.date)}</p></div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="materi" className="mt-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {materials.map((m) => (
                <div key={m.id} className="bg-white border border-slate-200 rounded-xl p-5 flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-navy/5 text-navy flex items-center justify-center shrink-0"><Package className="h-6 w-6" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-brandred font-semibold">{m.category}</div>
                    <h4 className="font-bold text-navy">{m.title}</h4>
                    <p className="text-xs text-slate-400 font-mono">{m.file_name}</p>
                  </div>
                  {m.storage_path ? (
                    <a href={`${API}/files/${m.storage_path}`} download data-testid="media-material-download">
                      <Button size="sm" className="bg-brandred hover:bg-brandred-700 text-white"><Download className="h-4 w-4 mr-1" /> Unduh</Button>
                    </a>
                  ) : (
                    <Button size="sm" variant="outline" onClick={notify}><Download className="h-4 w-4" /></Button>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="kontak" className="mt-6">
            <div className="bg-navy text-white rounded-2xl p-8 max-w-lg">
              <h3 className="text-xl font-extrabold mb-1">Humas Bawaslu Sultra</h3>
              <p className="text-slate-300 text-sm mb-6">Untuk kebutuhan wawancara, konfirmasi, dan liputan media.</p>
              <div className="space-y-4">
                <div className="flex items-center gap-3"><User className="h-5 w-5 text-brandred" /> Koordinator Humas &amp; Data Informasi</div>
                <div className="flex items-center gap-3"><Phone className="h-5 w-5 text-brandred" /> (0401) 3122345</div>
                <div className="flex items-center gap-3"><Mail className="h-5 w-5 text-brandred" /> humas.sultra@bawaslu.go.id</div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
